import React, { useState } from 'react';
import valuePropService, { ValuePropInventory } from '../services/valuePropService';
import { Link } from 'react-router-dom';
import gtmService from '../services/gtmService';
import { gtmPfad } from '../vertrieb/gtmLinks';
import MarkdownView from '../components/MarkdownView';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

const tierColor = (t: number): string => (t === 1 ? 'var(--sec-success)' : t === 2 ? 'var(--sec-warning)' : 'var(--sec-fg-subtle)');
const tierLabel = (t: number): string => (t === 1 ? 'Tier 1 — damit führen' : t === 2 ? 'Tier 2 — unterstützend' : 'Tier 3 — Table Stakes');

const ValuePropPage: React.FC = () => {
  const [companyName, setCompanyName] = useState('');
  const [url, setUrl] = useState('');
  const [product, setProduct] = useState('');
  const [materials, setMaterials] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // Gerade gespeichert: direkt im GTM-Setup oeffnen.
  const [gespeichertId, setGespeichertId] = useState<string | null>(null);
  const [inv, setInv] = useState<ValuePropInventory | null>(null);
  const [raw, setRaw] = useState('');

  const handleExtract = async () => {
    if (!materials.trim() && !url.trim() && !product.trim()) {
      setError('Bitte Materialien, URL oder Produkt angeben');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setInfo('');
      setInv(null);
      setRaw('');
      const result = await valuePropService.extract({
        companyName: companyName || undefined,
        url: url || undefined,
        product: product || undefined,
        materials: materials || undefined,
      });
      if (result.inventory) setInv(result.inventory);
      else setRaw(result.raw || 'Kein strukturiertes Inventar erhalten.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Extraktion');
    } finally {
      setLoading(false);
    }
  };

  const saveAsOffer = async () => {
    if (!inv) return;
    try {
      setSaving(true);
      setError('');
      const tier1 = inv.valueProps.filter((v) => v.tier === 1).map((v) => v.valueProp);
      const neu = await gtmService.createOffer({
        name: `${inv.companyName || 'Angebot'} — Value Props`,
        oneLiner: inv.primaryValueProp,
        valueProps: inv.valueProps.map((v) => `[${v.type}] ${v.valueProp}`).join('\n'),
        outcomes: tier1.join('\n'),
      });
      setGespeichertId(neu?.id ?? null);
      setInfo('Als GTM-Angebot gespeichert (Status: Entwurf).');
      setTimeout(() => setInfo(''), 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern als Angebot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <PageHeader title="Nutzenversprechen (Value Props)" subtitle={routeById('value-props')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Extrahiert alle Value Propositions aus deinen Materialien, übersetzt Features in Outcomes
        und ordnet sie nach Typ, Persona, Kanal und Tier (powered by Skill <em>value-prop-lister</em>).
        Das Ergebnis lässt sich direkt als GTM-Angebot speichern.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {info && (
        <div className="alert alert-success">
          {info}
          {gespeichertId && <> <Link to={gtmPfad('offer', gespeichertId)}>Im GTM-Setup öffnen</Link></>}
        </div>
      )}

      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 220px' }}>
            <label className="form-label">Unternehmen</label>
            <input className="form-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: '1 1 220px' }}>
            <label className="form-label">Website-URL (optional)</label>
            <input className="form-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Produkt (kurz)</label>
          <input className="form-input" value={product} onChange={(e) => setProduct(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Materialien (Website-Copy, Pitch, Case Studies …)</label>
          <textarea className="form-textarea" rows={8} value={materials} onChange={(e) => setMaterials(e.target.value)} placeholder="Text hier einfügen — je mehr, desto besser." />
        </div>
        <button className="btn btn-success" onClick={handleExtract} disabled={loading}>
          {loading ? 'Extrahiere…' : 'Value Props extrahieren'}
        </button>
      </div>

      {raw && <MarkdownView content={raw} style={{ marginTop: '16px' }} />}

      {inv && (
        <div style={{ marginTop: '20px' }}>
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="card-header">
              <div className="card-title">Primäre Value Proposition</div>
              <button className="btn btn-primary btn-sm" onClick={saveAsOffer} disabled={saving}>
                {saving ? 'Speichere…' : 'Als GTM-Angebot speichern'}
              </button>
            </div>
            <div style={{ fontSize: '15px', color: 'var(--sec-fg)', marginTop: '8px' }}>{inv.primaryValueProp}</div>
            {inv.audience && <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '4px' }}>Zielgruppe: {inv.audience}</div>}
          </div>

          {inv.valueProps.map((v, i) => (
            <div className="card" key={i} style={{ borderLeft: `4px solid ${tierColor(v.tier)}`, marginBottom: '10px' }}>
              <div className="card-header">
                <div className="card-title">{v.valueProp}</div>
                <span style={{ fontSize: '12px', color: tierColor(v.tier), fontWeight: 600 }}>{tierLabel(v.tier)}</span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '6px' }}>
                <div><strong>Typ:</strong> {v.type}</div>
                {v.evidence && <div><strong>Beleg:</strong> {v.evidence}</div>}
                {v.bestFor && <div><strong>Am besten für:</strong> {v.bestFor}</div>}
                {v.useWhen && <div><strong>Einsetzen wenn:</strong> {v.useWhen}</div>}
              </div>
            </div>
          ))}

          {(inv.byChannel.emailSubjects.length > 0 || inv.byChannel.emailOpener || inv.byChannel.linkedinMessage) && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="card-title" style={{ marginBottom: '6px' }}>Schnellreferenz nach Kanal</div>
              <div style={{ fontSize: '13px', color: 'var(--sec-fg)' }}>
                {inv.byChannel.emailSubjects.length > 0 && (
                  <div><strong>E-Mail-Betreffs:</strong> {inv.byChannel.emailSubjects.join(' · ')}</div>
                )}
                {inv.byChannel.emailOpener && <div><strong>E-Mail-Opener:</strong> {inv.byChannel.emailOpener}</div>}
                {inv.byChannel.linkedinMessage && <div><strong>LinkedIn:</strong> {inv.byChannel.linkedinMessage}</div>}
              </div>
            </div>
          )}

          {inv.summary && <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)' }}>{inv.summary}</div>}
        </div>
      )}
    </div>
  );
};

export default ValuePropPage;
